import { Navigate, Outlet, useLocation } from 'react-router-dom';

import ErrorPage from '~components/errorBoundary/errorPage';
import ErrorType from '~components/errorBoundary/errorType';
import pagePaths from '~constants/pagePaths';
import { UserRoleEnum } from '~enums';
import { useSession } from '~hooks';
import useAuthorize from '~hooks/useAuthorize';
import { GeneralLoaderWrapper } from '~layouts';

interface ProtectedRouteProps {
	allowedRoles?: UserRoleEnum[];
}

/**
 * Protected route that checks authentication and role-based authorization.
 *
 * Note: By default every role is allowed. If no roles are specified in the implementation
 * any role will be allowed to view the page, if they are authorized.
 */
const ProtectedRoute = ({
	allowedRoles = [
		UserRoleEnum.AllowAnonymous,
		UserRoleEnum.Manufacturing,
		UserRoleEnum.ClientAdmin,
		UserRoleEnum.User,
		UserRoleEnum.SuperAdmin,
		UserRoleEnum.Support,
	],
}: ProtectedRouteProps) => {
	const location = useLocation();
	const { authorize } = useAuthorize();
	const session = useSession();

	if (session.isLoading) {
		return <GeneralLoaderWrapper />;
	}

	// If we don't have a valid session, we redirect to the login page.
	if (!session.isLoading && !session.hasValidSession) {
		console.info(
			'[Auth] Required valid session not found',
			session.isLoading,
			session.hasValidSession
		);
		return <Navigate to={pagePaths.AuthLogin} state={{ from: location.pathname }} replace />;
	}

	// Check if the user is allowed to view the route based on it's Skopei Connect role.
	if (!authorize(allowedRoles)) {
		return <ErrorPage type={ErrorType.NotAuthorized} />;
	}

	// All checks passed, show the page.
	return <Outlet />;
};

export default ProtectedRoute;
